import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Box,
    Typography,
    Slider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import API_URL from '../config';
import ImagePreview from './ImagePreview';

const VectorizeModal = ({ open, onClose, onImport }) => {
    const [sourceFile, setSourceFile] = useState(null);
    const [sourceImg, setSourceImg] = useState(null);
    const [processedImg, setProcessedImg] = useState(null);
    const [svgContent, setSvgContent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [params, setParams] = useState({
        threshold: 127,
        blur: 0,
        zoom: 1.0,
        sharpen: 0.0,
        mergeDist: 0.0,
        skeletonType: 'none',
        smoothness: 'none'
    });
    const [strokeWidth, setStrokeWidth] = useState(0.5);

    const applyStrokeWidth = (svg, width) => {
        if (!svg) return null;
        return svg.replace(/stroke-width="[^"]*"/g, `stroke-width="${width}"`);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSourceFile(file);
            setProcessedImg(null);
            setSvgContent(null);
            const reader = new FileReader();
            reader.onload = (e) => setSourceImg(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const processImage = async () => {
        if (!sourceFile) return;

        setIsLoading(true);
        const formData = new FormData();
        formData.append('file', sourceFile);
        formData.append('threshold', params.threshold);
        formData.append('blur', params.blur);
        formData.append('zoom', params.zoom);
        formData.append('sharpen', params.sharpen);
        formData.append('merge_dist', params.mergeDist);
        formData.append('skeletonization_type', params.skeletonType);
        formData.append('smoothness', params.smoothness);

        try {
            const response = await fetch(`${API_URL}/api/process`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const text = await response.text();
                setSvgContent(text);
                const updatedSvg = applyStrokeWidth(text, strokeWidth);
                const blob = new Blob([updatedSvg], { type: 'image/svg+xml' });
                if (processedImg) URL.revokeObjectURL(processedImg);
                setProcessedImg(URL.createObjectURL(blob));
            } else {
                console.error('Processing failed');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!sourceFile) return;
        const timer = setTimeout(() => {
            processImage();
        }, 500); // Debounce
        return () => clearTimeout(timer);
    }, [params, sourceFile]);

    const handleImport = () => {
        if (svgContent) {
            const finalSvg = applyStrokeWidth(svgContent, strokeWidth);
            onImport(finalSvg);
            onClose();
        }
    };

    const updateParam = (name, value) => {
        setParams(prev => ({ ...prev, [name]: value }));
    };

    const handleStrokeWidthChange = (value) => {
        setStrokeWidth(value);
        if (svgContent) {
            const updatedSvg = applyStrokeWidth(svgContent, value);
            const blob = new Blob([updatedSvg], { type: 'image/svg+xml' });
            if (processedImg) URL.revokeObjectURL(processedImg);
            setProcessedImg(URL.createObjectURL(blob));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Import Image to Vector
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={3} sx={{ height: '60vh' }}>
                    {/* Controls Panel */}
                    <Box sx={{ width: '300px', overflowY: 'auto', pr: 2 }}>
                        <Stack spacing={2}>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                            >
                                Upload Image
                                <input type="file" hidden accept="image/*" onChange={handleFileUpload} />
                            </Button>

                            <Box>
                                <Typography variant="caption" gutterBottom>Threshold ({params.threshold})</Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={255}
                                    value={params.threshold}
                                    onChange={(e, v) => updateParam('threshold', v)}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" gutterBottom>Blur ({params.blur})</Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={10}
                                    value={params.blur}
                                    onChange={(e, v) => updateParam('blur', v)}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" gutterBottom>Zoom ({params.zoom}x)</Typography>
                                <Slider
                                    size="small"
                                    min={0.1}
                                    max={5}
                                    step={0.1}
                                    value={params.zoom}
                                    onChange={(e, v) => updateParam('zoom', v)}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" gutterBottom>Sharpen ({params.sharpen})</Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={5}
                                    step={0.1}
                                    value={params.sharpen}
                                    onChange={(e, v) => updateParam('sharpen', v)}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" gutterBottom>Merge Dist ({params.mergeDist})</Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={10}
                                    step={0.5}
                                    value={params.mergeDist}
                                    onChange={(e, v) => updateParam('mergeDist', v)}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" gutterBottom>Stroke Width ({strokeWidth})</Typography>
                                <Slider
                                    size="small"
                                    min={0.1}
                                    max={5}
                                    step={0.1}
                                    value={strokeWidth}
                                    onChange={(e, v) => handleStrokeWidthChange(v)}
                                />
                            </Box>

                            <FormControl fullWidth size="small">
                                <InputLabel>Skeleton Type</InputLabel>
                                <Select
                                    value={params.skeletonType}
                                    label="Skeleton Type"
                                    onChange={(e) => updateParam('skeletonType', e.target.value)}
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="morphological">Morphological</MenuItem>
                                    <MenuItem value="zhang-suen">Zhang-Suen</MenuItem>
                                    <MenuItem value="guo-hall">Guo-Hall</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Smoothness</InputLabel>
                                <Select
                                    value={params.smoothness}
                                    label="Smoothness"
                                    onChange={(e) => updateParam('smoothness', e.target.value)}
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="low">Low</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="high">High</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </Box>

                    {/* Preview Panel */}
                    <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                        <ImagePreview 
                            src={sourceImg} 
                            label="Source" 
                        />
                        <ImagePreview 
                            src={processedImg} 
                            label="Vectorized" 
                            isLoading={isLoading} 
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleImport} variant="contained" disabled={!svgContent || isLoading}>
                    Import to Project
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VectorizeModal;
